import type { SignalPayload, TrackKind } from '@etown/shared';

export interface PeerSessionCallbacks {
  /** Signal an die Gegenseite (laeuft ueber den Socket.IO-Relay). */
  sendSignal(payload: SignalPayload): void;
  /** mid->kind-Zuordnung an die Gegenseite melden. */
  sendTrackMeta(mid: string, kind: TrackKind): void;
  /** Remote-Stream einer Track-Art kam an bzw. fiel weg. */
  onRemoteStream(kind: TrackKind, stream: MediaStream | null): void;
  onConnectionState(state: 'connecting' | 'connected' | 'failed'): void;
}

/**
 * Eine RTCPeerConnection zu genau einem Peer, verhandelt nach dem
 * Perfect-Negotiation-Muster (WHATWG): beide Seiten duerfen jederzeit
 * Offers ausloesen; Kollisionen (Glare) loest die "polite" Seite per
 * Rollback auf. polite/impolite ist deterministisch ueber die Socket-IDs.
 *
 * Track-Zuordnung: SDP sagt nur "audio/video", nicht "Kamera oder Screen".
 * Daher meldet der Sender pro Transceiver-mid die Art ueber den Socket
 * (webrtc:track-meta); der Empfaenger puffert, bis Track UND Meta da sind.
 */
export class PeerSession {
  private pc: RTCPeerConnection;
  private makingOffer = false;
  private ignoreOffer = false;
  private senders = new Map<TrackKind, RTCRtpSender>();
  private announcedMids = new Map<string, TrackKind>();
  private remoteKindByMid = new Map<string, TrackKind>();
  private pendingRemoteTracks = new Map<string, MediaStreamTrack>();
  private remoteStreams = new Map<TrackKind, MediaStream>();
  private closed = false;

  constructor(
    readonly peerId: string,
    private readonly polite: boolean,
    iceServers: RTCIceServer[],
    private readonly callbacks: PeerSessionCallbacks,
  ) {
    this.pc = new RTCPeerConnection({ iceServers });

    this.pc.onnegotiationneeded = async () => {
      try {
        this.makingOffer = true;
        await this.pc.setLocalDescription();
        this.signalDescription();
      } catch {
        // Verbindung im Abbau — ignorieren
      } finally {
        this.makingOffer = false;
      }
    };

    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.callbacks.sendSignal({ candidate: event.candidate.toJSON() });
      }
    };

    this.pc.onconnectionstatechange = () => {
      const state = this.pc.connectionState;
      if (state === 'connected') this.callbacks.onConnectionState('connected');
      else if (state === 'failed') this.callbacks.onConnectionState('failed');
      else if (state === 'connecting') this.callbacks.onConnectionState('connecting');
    };

    this.pc.ontrack = (event) => {
      const mid = event.transceiver.mid;
      if (!mid) return;
      const kind = this.remoteKindByMid.get(mid);
      if (kind) this.acceptRemoteTrack(kind, event.track);
      else this.pendingRemoteTracks.set(mid, event.track);
    };

    // Ohne lokale Tracks gaebe es keine Verhandlung — der Initiator oeffnet
    // einen Datenkanal, damit die Verbindung sofort steht (und spaeteres
    // Audio/Video ohne neuen ICE-Aufbau startet).
    if (!polite) this.pc.createDataChannel('etown-keepalive');
  }

  /** Eingehendes Signal (Description oder ICE-Kandidat) verarbeiten. */
  async handleSignal(payload: SignalPayload): Promise<void> {
    if (this.closed) return;
    try {
      if (payload.description) {
        const description = payload.description as RTCSessionDescriptionInit;
        const offerCollision =
          description.type === 'offer' && (this.makingOffer || this.pc.signalingState !== 'stable');
        this.ignoreOffer = !this.polite && offerCollision;
        if (this.ignoreOffer) return;

        await this.pc.setRemoteDescription(description);
        if (description.type === 'offer') {
          await this.pc.setLocalDescription();
          this.signalDescription();
        }
      } else if (payload.candidate) {
        try {
          await this.pc.addIceCandidate(payload.candidate as RTCIceCandidateInit);
        } catch (err) {
          if (!this.ignoreOffer) throw err;
        }
      }
    } catch {
      // Verhandlungsfehler einer einzelnen Runde sind nicht fatal —
      // die naechste onnegotiationneeded-Runde repariert den Zustand.
    }
  }

  /** mid->kind-Meldung der Gegenseite. */
  handleTrackMeta(mid: string, kind: TrackKind): void {
    this.remoteKindByMid.set(mid, kind);
    const pending = this.pendingRemoteTracks.get(mid);
    if (pending) {
      this.pendingRemoteTracks.delete(mid);
      this.acceptRemoteTrack(kind, pending);
    }
  }

  /** Lokalen Track setzen/entfernen (Mic/Cam/Screen). */
  async setTrack(kind: TrackKind, track: MediaStreamTrack | null): Promise<void> {
    if (this.closed) return;
    const existing = this.senders.get(kind);
    if (!track) {
      if (existing) {
        // removeTrack statt replaceTrack(null): loest eine Renegotiation aus,
        // wodurch die Gegenseite zuverlaessig ein mute/ended am Track sieht
        this.pc.removeTrack(existing);
        this.senders.delete(kind);
      }
      return;
    }
    if (existing) {
      await existing.replaceTrack(track);
      this.announceMids();
      return;
    }
    const sender = this.pc.addTrack(track);
    this.senders.set(kind, sender);
    // mid steht erst nach der Verhandlung fest -> announceMids laeuft
    // zusaetzlich nach jedem setLocalDescription (signalDescription)
  }

  /**
   * Sende-Qualitaet eines Video-Kinds setzen (Mesh-Bandbreitensteuerung).
   * Laeuft pro Verbindung ueber RTCRtpSender.setParameters — bewusst nicht
   * ueber track.applyConstraints, das wuerde die Kamera global umkonfigurieren.
   */
  async setVideoQuality(
    kind: 'cam' | 'screen',
    opts: { scaleResolutionDownBy?: number; maxBitrate: number; maxFramerate?: number },
  ): Promise<void> {
    if (this.closed) return;
    const sender = this.senders.get(kind);
    if (!sender) return;
    try {
      const parameters = sender.getParameters();
      // Firefox liefert anfangs leere encodings — dann selbst anlegen
      if (!parameters.encodings || parameters.encodings.length === 0) parameters.encodings = [{}];
      const encoding = parameters.encodings[0]!;
      encoding.maxBitrate = opts.maxBitrate;
      if (opts.scaleResolutionDownBy !== undefined) encoding.scaleResolutionDownBy = opts.scaleResolutionDownBy;
      if (opts.maxFramerate !== undefined) encoding.maxFramerate = opts.maxFramerate;
      await sender.setParameters(parameters);
    } catch {
      // Nicht fatal — der naechste Tier-Wechsel setzt die Werte erneut
    }
  }

  close(): void {
    this.closed = true;
    this.pc.onnegotiationneeded = null;
    this.pc.onicecandidate = null;
    this.pc.ontrack = null;
    this.pc.onconnectionstatechange = null;
    this.pc.close();
    for (const kind of [...this.remoteStreams.keys()]) {
      this.callbacks.onRemoteStream(kind, null);
    }
    this.remoteStreams.clear();
  }

  private signalDescription(): void {
    if (this.pc.localDescription) {
      this.callbacks.sendSignal({
        description: {
          type: this.pc.localDescription.type,
          sdp: this.pc.localDescription.sdp,
        },
      });
      this.announceMids();
    }
  }

  /** Allen eigenen Sendern ihre mids zuordnen und (einmalig) melden. */
  private announceMids(): void {
    for (const [kind, sender] of this.senders) {
      const transceiver = this.pc.getTransceivers().find((t) => t.sender === sender);
      const mid = transceiver?.mid;
      if (mid && this.announcedMids.get(mid) !== kind) {
        this.announcedMids.set(mid, kind);
        this.callbacks.sendTrackMeta(mid, kind);
      }
    }
  }

  private acceptRemoteTrack(kind: TrackKind, track: MediaStreamTrack): void {
    const stream = new MediaStream([track]);
    this.remoteStreams.set(kind, stream);
    this.callbacks.onRemoteStream(kind, stream);
    const dropped = (): void => {
      this.remoteStreams.delete(kind);
      this.callbacks.onRemoteStream(kind, null);
    };
    track.addEventListener('ended', dropped);
    track.addEventListener('mute', dropped);
    track.addEventListener('unmute', () => {
      const restored = new MediaStream([track]);
      this.remoteStreams.set(kind, restored);
      this.callbacks.onRemoteStream(kind, restored);
    });
  }
}
